import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import {
  ListGroup,
  Row,
  Col,
  Form,
  OverlayTrigger,
  Tooltip,
  Button,
  Spinner,
} from 'react-bootstrap'
import AdExchangeChart from './AdExchangeChart'

function ReportListLink({ setting }) {
  const { data = [], fieldLabelMap = {}, names = {} } = setting

  const [siteFilter, setSiteFilter] = useState('ALL')
  const [adUnitFilter, setAdUnitFilter] = useState('ALL')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [isDownloading, setIsDownloading] = useState(false)

  console.log('ReportListLink - data[0]', data[0])
  const allKeys = Object.keys(data[0]).filter((key) => !key.includes('ID'))
  console.log('ReportListLink - allKeys', allKeys)
  const dimensionKeys = allKeys.filter(
    (k) => k.startsWith('Dimension.') || k.startsWith('CF')
  )
  console.log('ReportListLink - dimensionKeys', dimensionKeys)
  console.log('ReportListLink - fieldLabelMap', fieldLabelMap)
  const columnKeys = allKeys.filter(
    (k) => !k.startsWith('Dimension.') && !k.startsWith('CF')
  )
  console.log('ReportListLink - columnKeys', columnKeys)
  const x1 = dimensionKeys.length * 2
  const x2 = columnKeys.length * 2

  const sites = useMemo(() => {
    const vals = new Set(
      data.map(
        (r) => r['Dimension.TOP_LEVEL_CUSTOM_TARGETING_KEY[13107349]_VALUE']
      )
    )
    return Array.from(vals).sort()
  }, [data])

  const adUnits = useMemo(() => {
    const vals = new Set(data.map((r) => r['Dimension.AD_UNIT_NAME']))
    return Array.from(vals).sort()
  }, [data])

  const filtered = useMemo(
    () =>
      data.filter((row) => {
        const site =
          row['Dimension.TOP_LEVEL_CUSTOM_TARGETING_KEY[13107349]_VALUE']
        const ad = row['Dimension.AD_UNIT_NAME']
        const date = row['Dimension.DATE']
        if (siteFilter !== 'ALL' && site !== siteFilter) return false
        if (adUnitFilter !== 'ALL' && ad !== adUnitFilter) return false
        if (dateFrom && date < dateFrom) return false
        if (dateTo && date > dateTo) return false
        return true
      }),
    [data, siteFilter, adUnitFilter, dateFrom, dateTo]
  )

  const aggregated = useMemo(() => {
    const byDay = {}
    filtered.forEach((row) => {
      const date = row['Dimension.DATE']
      if (!byDay[date]) {
        byDay[date] = { date, impressions: 0, clicks: 0, revenue: 0 }
      }
      byDay[date].impressions += row.impression
      byDay[date].revenue += row.revenue
    })
    return Object.values(byDay).sort((a, b) => a.date.localeCompare(b.date))
  }, [filtered])

  const handleDownloadFiltered = () => {
    setIsDownloading(true)

    const labelForKey = (key) =>
      names[fieldLabelMap[key]] || fieldLabelMap[key] || key

    const formatDimensionValue = (row, key) => names[row[key]] || row[key] || ''

    const formatColumnValue = (row, key) => {
      const namedValue = names[row[key]]
      if (
        key === 'Column.AD_EXCHANGE_LINE_ITEM_LEVEL_REVENUE' ||
        key === 'AdX 收益'
      ) {
        if (namedValue) return namedValue
        const numericValue = Number(row[key])
        return Number.isFinite(numericValue)
          ? numericValue.toFixed(2)
          : row[key] || ''
      }
      return namedValue || row[key] || ''
    }

    const escapeCSV = (value) => {
      if (value === null || value === undefined) return ''
      const stringValue = String(value)
      if (
        stringValue.includes(',') ||
        stringValue.includes('"') ||
        stringValue.includes('\n')
      ) {
        return `"${stringValue.replace(/"/g, '""')}"`
      }
      return stringValue
    }

    // 組合 CSV 字串
    const headers = [
      ...dimensionKeys.map((key) => labelForKey(key)),
      ...columnKeys.map((key) => labelForKey(key)),
    ]
    const csvRows = [headers.join(',')]
    // [
    //   '網站,日期,廣告單元名稱,委刊項,AdX 曝光次數,AdX 點擊次數,AdX 收益',
    // ]

    filtered.forEach((row) => {
      const dimensionValues = dimensionKeys.map((key) =>
        escapeCSV(formatDimensionValue(row, key))
      )
      const columnValues = columnKeys.map((key) =>
        escapeCSV(formatColumnValue(row, key))
      )

      csvRows.push([...dimensionValues, ...columnValues].join(','))
      // [
      //   '網站,日期,廣告單元名稱,委刊項,AdX 曝光次數,AdX 點擊次數,AdX 收益',    // 表頭
      //   'news_site,2025-01-15,Homepage_Top,Q1_Premium,12345,678,3456.78',               // 第一筆 row
      //   'travel_site,2025-01-16,Article_Mid,Travel_Promo,9876,543,2100.00',             // 第二筆 row
      // ]
    })

    const csvString = csvRows.join('\n')
    // 加入 BOM 以確保 Excel 正確顯示中文
    const BOM = '\uFEFF'
    const csvWithBOM = BOM + csvString

    // 建立 Blob 並下載
    const blob = new Blob([csvWithBOM], {
      type: 'text/csv;charset=utf-8;',
    })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    const fileName = `report_${new Date().toISOString().split('T')[0]}.csv`
    link.download = fileName
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)

    setIsDownloading(false)
  }

  return (
    <div
      className="flex-nowrap text-nowrap"
      style={{
        maxHeight: 'calc(100vh - 32px)',
        overflow: 'scroll',
        padding: '8px',
        paddingTop: '0px',
        // overflowX: 'hidden',
      }}
    >
      <Form
        className="mb-3"
        style={{
          position: 'sticky',
          left: '0px',
        }}
      >
        <Row className="g-2 align-items-end mt-3">
          <Col xs={3}>
            <Form.Group controlId="siteFilter">
              <Form.Label>Website</Form.Label>
              <Form.Select
                value={siteFilter}
                onChange={(e) => setSiteFilter(e.target.value)}
              >
                <option value="ALL">All</option>
                {sites.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col xs={3}>
            <Form.Group controlId="adUnitFilter">
              <Form.Label>Ad Unit</Form.Label>
              <Form.Select
                value={adUnitFilter}
                onChange={(e) => setAdUnitFilter(e.target.value)}
              >
                <option value="ALL">All</option>
                {adUnits.map((u) => (
                  <option key={u} value={u}>
                    {u}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col>
            <Form.Group controlId="dateFrom">
              <Form.Label>From</Form.Label>
              <Form.Control
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col>
            <Form.Group controlId="dateTo">
              <Form.Label>To</Form.Label>
              <Form.Control
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col xs="auto">
            <Button
              variant="primary"
              onClick={handleDownloadFiltered}
              disabled={isDownloading}
            >
              {isDownloading ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  處理中...
                </>
              ) : (
                '下載篩選資料'
              )}
            </Button>
          </Col>
        </Row>
      </Form>
      <Row
        style={{
          position: 'sticky',
          left: '0px',
        }}
      >
        <AdExchangeChart
          setting={{
            data: aggregated,
          }}
        />
      </Row>
      <Row
        className="flex-nowrap"
        style={{
          position: 'sticky',
          top: '0px',
          height: '40px',
          zIndex: '999',
        }}
      >
        <Col xs={x1} style={{ backgroundColor: 'white' }}>
          <Row className="h-100 flex-nowrap">
            <h6
              style={{
                fontWeight: 'bold',
                marginTop: '10px',
                backgroundColor: 'white',
              }}
            >
              📐 維度
            </h6>
          </Row>
        </Col>
        <Col xs={x2} style={{ backgroundColor: 'white' }}>
          <Row className="h-100 flex-nowrap">
            <h6
              style={{
                fontWeight: 'bold',
                marginTop: '10px',
              }}
            >
              📊 指標
            </h6>
          </Row>
        </Col>
      </Row>
      <Row
        className="flex-nowrap"
        style={{
          position: 'sticky',
          height: '40px',
          top: '40px',
          zIndex: '999',
          fontWeight: 'bold',
        }}
      >
        <Col xs={x1}>
          <Row className="h-100 flex-nowrap">
            {dimensionKeys.map((key, idx) => (
              <Col
                className="d-flex"
                style={{ backgroundColor: '#f8f9fa' }}
                key={idx}
              >
                <p className="m-auto">
                  {names[fieldLabelMap[key]] || fieldLabelMap[key] || key}
                </p>
              </Col>
            ))}
          </Row>
        </Col>
        <Col xs={x2}>
          <Row className="h-100 flex-nowrap">
            {columnKeys.map((key, idx) => (
              <Col
                className="d-flex"
                style={{ backgroundColor: '#f8f9fa' }}
                key={idx}
              >
                <p className="m-auto">
                  {names[fieldLabelMap[key]] || fieldLabelMap[key] || key}
                </p>
              </Col>
            ))}
          </Row>
        </Col>
      </Row>
      <Row>
        <Col
          style={{
            overflow: 'visible',
          }}
        >
          <Row className="flex-nowrap">
            <Col xs={x1} className="px-0">
              <ListGroup variant="flush">
                {filtered.map((row, rowIndex) => (
                  <ListGroup.Item key={`dim-${rowIndex}`}>
                    <Row className="flex-nowrap">
                      {dimensionKeys.map((key, idx) => {
                        const displayValue = names[row[key]] || row[key]
                        const isLineItemName =
                          key === 'Dimension.LINE_ITEM_NAME'

                        if (
                          isLineItemName &&
                          displayValue &&
                          displayValue.length > 15
                        ) {
                          const truncatedValue = `${displayValue.substring(
                            0,
                            15
                          )}...`
                          return (
                            <Col className="text-nowrap flex-grow-1" key={idx}>
                              <OverlayTrigger
                                placement="top"
                                overlay={
                                  <Tooltip id={`tooltip-${rowIndex}-${idx}`}>
                                    {displayValue}
                                  </Tooltip>
                                }
                              >
                                <span style={{ cursor: 'help' }}>
                                  {truncatedValue}
                                </span>
                              </OverlayTrigger>
                            </Col>
                          )
                        }

                        return (
                          <Col className="text-nowrap flex-grow-1" key={idx}>
                            {displayValue}
                          </Col>
                        )
                      })}
                    </Row>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </Col>
            <Col xs={x2} className="px-0">
              <ListGroup variant="flush">
                {filtered.map((row, rowIndex) => (
                  <ListGroup.Item key={`col-${rowIndex}`}>
                    <Row className="flex-nowrap">
                      {columnKeys.map((key, idx) => (
                        <Col className="text-nowrap flex-grow-1" key={idx}>
                          {key ===
                            'Column.AD_EXCHANGE_LINE_ITEM_LEVEL_REVENUE' ||
                          key === 'AdX 收益'
                            ? names[row[key]] || Number(row[key]).toFixed(2)
                            : names[row[key]] || row[key]}
                        </Col>
                      ))}
                    </Row>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </Col>
          </Row>
        </Col>
      </Row>
    </div>
  )
}

ReportListLink.propTypes = {
  setting: PropTypes.shape().isRequired,
}

export default ReportListLink
